/**
 * Testimonials Section
 *
 * Displays customer testimonials on the landing page using the Testimonials block.
 * Features real customer quotes with ratings and avatars.
 *
 * @example
 * ```tsx
 * <TestimonialsSection />
 * ```
 */

import {type Testimonial, Testimonials, type TestimonialsCTA} from '@/ui';
import {motion} from 'framer-motion';

// =============================================================================
// TESTIMONIAL DATA
// =============================================================================

/**
 * Customer testimonials for the landing page.
 * These should be replaced with real customer testimonials when available.
 */
const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Emma Wilson',
    role: 'Gift Shopper',
    quote:
      "I turned my daughter's drawing into a beautiful figurine. The whole family was amazed! It's now her most treasured possession.",
    rating: 5,
    featured: true,
  },
  {
    id: '2',
    name: 'Marcus Chen',
    role: 'Pet Owner',
    quote:
      'Created a figurine of my dog from just a photo. The detail is incredible - even captured his little quirks perfectly.',
    rating: 5,
  },
  {
    id: '3',
    name: 'Sofia Andersson',
    role: 'Anniversary Gift',
    quote:
      'Gave my parents a figurine of their wedding photo for their 40th anniversary. They cried happy tears!',
    rating: 5,
  },
  {
    id: '4',
    name: 'James Taylor',
    role: 'Creator',
    quote:
      "The AI understood exactly what I described. From text to a physical figurine in days - it felt like magic.",
    rating: 4,
  },
  {
    id: '5',
    name: 'Mia Johnson',
    role: 'Collector',
    quote:
      "Quality is outstanding. I've ordered three figurines now and each one exceeds expectations.",
    rating: 5,
  },
  {
    id: '6',
    name: 'Alex Rivera',
    role: 'Artist',
    quote:
      'As an artist, I was skeptical. But seeing my sketches transformed into 3D figurines? Absolutely blown away.',
    rating: 5,
  },
];

// =============================================================================
// CTA CONFIGURATION
// =============================================================================

const primaryCta: TestimonialsCTA = {
  label: 'Create Your Own',
  ariaLabel: 'Start creating your own custom figurine today',
  href: '/create',
  variant: 'primary',
};

const secondaryCta: TestimonialsCTA = {
  label: 'See Examples',
  ariaLabel: 'View example figurines created by our customers',
  href: '#gallery',
  variant: 'outline',
};

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * TestimonialsSection displays customer testimonials with ratings.
 * Uses the Testimonials block from @/ui for consistent styling.
 * Wrapped with framer-motion for scroll-triggered animations.
 */
export function TestimonialsSection(): React.ReactElement {
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{
        duration: 1.2,
        ease: [0.16, 1, 0.3, 1],
      }}
    >
      <Testimonials
        testimonials={testimonials}
        title="Loved by Thousands"
        subtitle="Join our community of happy creators who've brought their ideas to life"
        primaryCta={primaryCta}
        secondaryCta={secondaryCta}
        layout="grid"
        background="gradient"
        spacing="lg"
        maxItems={6}
        aria-label="Customer testimonials"
        data-testid="landing-testimonials"
      />
    </motion.div>
  );
}
